import { Options } from '@b2chat/chat-center-sdk';
import B2ChatClient from '@client-sdk';
import { appConfig } from '@src/config/appConfig';
import {
  fetchChatsBotFailure,
  fetchChatsBotFulfill,
  fetchChatsBotSuccess,
  fetchChatSideTotals,
  fetchChatsOnHoldFailure,
  fetchChatsOnHoldFulfill,
  fetchChatsOnHoldSuccess,
  fetchChatsRequestFailure,
  fetchChatsRequestFulfill,
  fetchChatsRequestSuccess,
  fetchChatTotals,
  fetchCRMbutton,
  fetchCRMbuttonFailure,
  fetchCRMbuttonSuccess,
  fetchCRMinfo,
  fetchCRMinfoFailure,
  fetchCRMinfoSuccess,
  fetchNotificationTimeLimit,
  fetchNotificationTimeLimitFailure,
  fetchSearchChats,
  fetchSegmentsFailure,
  fetchSegmentsSuccess,
  fetchStartConsole,
  fetchTransformTextAI,
  fetchTransformTextAIFailure,
  fetchTransformTextAISuccess,
  increaseOffsetChatsBot,
  increaseOffsetChatsOnHold,
  increaseOffsetChatsRequest,
  setNotificationTimeLimit,
} from '@src/reducers/chats';
import {
  ActionMode,
  ChatsState,
  initialTimeLimit,
  NotificationTimeLimit,
  Segments,
} from '@src/reducers/chats/initialState';
import { chatSearchActions, ChatsFilters } from '@src/reducers/chatSearch';
import {
  activeChatSideSelector,
  isFirstTrayChatsOnHoldSelector,
} from '@src/selectors/chats';
import {
  activeSideFiltersSelector,
  chatsSideFiltersSelector,
  filtersToRequestPayloadSelector,
} from '@src/selectors/chatSearch';
import { getConsolePreferenceShowBotsInbox } from '@src/selectors/preferences';
import type { RootState } from '@src/store/reduxstore';
import {
  ChatSideType,
  ChatsResponse,
  NotificationType,
  ResponseStatus,
} from '@src/types/chats';
import { ErrorData } from '@types';
import { B2ChatAPI } from '@types-api';
import {
  all,
  call,
  debounce,
  fork,
  put,
  select,
  takeLatest,
  takeLeading,
  throttle,
} from 'redux-saga/effects';
import { fetchChatSideTotalsSaga, fetchChatTotalsSaga } from './chatTotals';

export function* startFetchChatsOnHold(overrideOffset?: number) {
  const filtersPayload: ChatsFilters = yield select(
    filtersToRequestPayloadSelector
  );
  const options: Options = yield select(state => ({
    params: {
      limit: state.chats.chatsOnHold.limit,
      offset: overrideOffset ?? state.chats.chatsOnHold.offset,
    },
    data: {
      type: ChatSideType.ON_HOLD,
      ...filtersPayload,
    },
  }));

  try {
    const isFirstTrayChatsOnHold: boolean = yield select(
      isFirstTrayChatsOnHoldSelector
    );

    if (!isFirstTrayChatsOnHold)
      yield call(fetchChatSideTotalsSaga, ChatSideType.ON_HOLD, filtersPayload);

    const { error, data }: B2ChatAPI.Response<ChatsResponse> = yield call(
      B2ChatClient.resources.agentConsole.actions.chats,
      options
    );

    if (error) {
      yield put(fetchChatsOnHoldFailure(error));
    } else {
      yield put(
        fetchChatsOnHoldSuccess({
          tray: data.filteredChats,
          totalChats: data.totalChats,
          activeChat: appConfig.getData('activeChat'),
        })
      );
    }
  } catch (error) {
    yield put(fetchChatsOnHoldFailure(error as ErrorData));
  } finally {
    yield put(fetchChatsOnHoldFulfill());
  }
}

function* startFetchChatsRequestSaga() {
  const filtersPayload: Record<string, unknown> = yield select(
    filtersToRequestPayloadSelector
  );

  const options: Options = yield select(state => ({
    params: {
      limit: state.chats.chatsRequest.limit,
      offset: state.chats.chatsRequest.offset,
    },
    data: {
      type: ChatSideType.REQUEST,
      ...filtersPayload,
    },
  }));
  try {
    const { error, data }: B2ChatAPI.Response<ChatsResponse> = yield call(
      B2ChatClient.resources.agentConsole.actions.chats,
      options
    );

    if (error) {
      yield put(fetchChatsRequestFailure(error));
    } else {
      yield put(
        fetchChatsRequestSuccess({
          tray: data.filteredChats,
          totalChats: data.totalChats,
        })
      );
    }
  } catch (error) {
    yield put(fetchChatsRequestFailure(error as ErrorData));
  } finally {
    yield put(fetchChatsRequestFulfill());
  }
}

function* startFetchChatsBotSaga() {
  const filtersPayload: Record<string, unknown> = yield select(
    filtersToRequestPayloadSelector
  );
  const options: Options = yield select(state => ({
    params: {
      limit: state.chats.chatsBot.limit,
      offset: state.chats.chatsBot.offset,
    },
    data: {
      type: ChatSideType.BOT,
      ...filtersPayload,
    },
  }));
  try {
    const { error, data }: B2ChatAPI.Response<ChatsResponse> = yield call(
      B2ChatClient.resources.agentConsole.actions.chats,
      options
    );

    if (error) {
      yield put(fetchChatsBotFailure(error));
    } else {
      yield put(
        fetchChatsBotSuccess({
          tray: data.filteredChats,
          totalChats: data.totalChats,
        })
      );
    }
  } catch (error) {
    yield put(fetchChatsBotFailure(error as ErrorData));
  } finally {
    yield put(fetchChatsBotFulfill());
  }
}

function* fetchNotificationTimeLimitSaga(
  action: ReturnType<typeof fetchNotificationTimeLimit>
) {
  const { mode, notificationId, limit, status, type } = action.payload;
  let data;
  if (mode === ActionMode.UPDATE) {
    data = { status, noResponseTimeLimit: limit };
  }

  if (mode === ActionMode.CREATE) {
    data = { status, noResponseTimeLimit: limit, type };
  }

  try {
    const response: B2ChatAPI.Response<NotificationTimeLimit[]> = yield call(
      [B2ChatClient.resources.notification.actions, mode],
      {
        params:
          mode === ActionMode.UPDATE ? { id: notificationId || '' } : undefined,
        data,
        transformResponse: notification =>
          mode === ActionMode.GET
            ? JSON.parse(notification)
            : [JSON.parse(notification)],
      }
    );

    if (response.error && !response.data) {
      yield put(fetchNotificationTimeLimitFailure());
    } else {
      const timeLimit = response.data.find(
        item => item.type === NotificationType.NO_RESPONSE_TIME_LIMIT
      );
      const notification = timeLimit || initialTimeLimit;
      const loading =
        mode !== ActionMode.GET
          ? ResponseStatus.UPDATED
          : ResponseStatus.SUCCESS;
      yield put(setNotificationTimeLimit({ ...notification, loading }));
    }
  } catch (error) {
    yield put(fetchNotificationTimeLimitFailure());
  }
}

function* fetchCRMbuttonSaga(action: ReturnType<typeof fetchCRMbutton>) {
  const chatId = action.payload;
  try {
    const response: B2ChatAPI.Response<boolean> = yield call(
      B2ChatClient.resources.chats.actions.share,
      { params: { chatId } }
    );
    if (response.error) {
      yield put(fetchCRMbuttonFailure());
    } else {
      yield put(fetchCRMbuttonSuccess(response.data));
    }
  } catch (error) {
    yield put(fetchCRMbuttonFailure());
  }
}

function* fetchCRMinfoSaga(action: ReturnType<typeof fetchCRMinfo>) {
  const chatId = action.payload;
  try {
    const response: B2ChatAPI.Response<boolean> = yield call(
      B2ChatClient.resources.chats.actions.send,
      {
        params: {
          chatId,
        },
      }
    );
    if (response.error) {
      yield put(fetchCRMbuttonFailure());
    } else {
      yield put(fetchCRMinfoSuccess());
    }
  } catch (error) {
    yield put(fetchCRMinfoFailure());
  }
}

function* fetchTransformTextAISaga(
  action: ReturnType<typeof fetchTransformTextAI>
) {
  let inputText: string = yield select(
    state => state.chats.writtenMessages[state.chats.activeChat.id]
  );

  if (inputText.trim().length > 0) {
    sessionStorage.setItem('inputText', inputText);
  } else {
    inputText = sessionStorage.getItem('inputText') || '';
  }

  try {
    const {
      language = 'spanish',
      command,
      apiName = 'claude',
    } = action.payload;

    const response: B2ChatAPI.Response<{
      outputMessage: string;
      outputText: string;
    }> = yield call(B2ChatClient.resources.ai.actions.redact, {
      params: { language, command, apiName },
      data: {
        inputText,
      },
    });

    if (response.data) {
      yield put(fetchTransformTextAISuccess(response.data.outputText));
    }
    if (response.error || response.data.outputMessage) {
      throw new Error('Fail to transform text with AI');
    }
  } catch (error: unknown) {
    yield put(
      fetchTransformTextAIFailure({
        details: (error as Error).message,
        errorCode: 'OPERATION_FAILED',
        timestamp: Date.now(),
        traceId: 'Transform text with AI',
      })
    );
  }
}

function* fetchSegmentsSaga() {
  try {
    const { error, data }: B2ChatAPI.Response<Segments[]> = yield call(
      B2ChatClient.resources.segments.actions.get
    );
    if (error) {
      yield put(fetchSegmentsFailure());
    } else {
      yield put(fetchSegmentsSuccess(data));
    }
  } catch (error) {
    yield put(fetchSegmentsFailure());
  }
}

function* fetchStartConsoleSaga() {
  let fetchChatsSidePromise;
  const chatSide: ChatSideType = yield select(activeChatSideSelector);

  if (chatSide === ChatSideType.REQUEST)
    fetchChatsSidePromise = startFetchChatsRequestSaga;
  else if (chatSide === ChatSideType.BOT)
    fetchChatsSidePromise = startFetchChatsBotSaga;
  else fetchChatsSidePromise = startFetchChatsOnHold;

  yield all([
    fork(fetchSegmentsSaga),
    call(fetchChatTotalsSaga),
    fork(fetchChatsSidePromise),
  ]);
}

function* fetchChatsSearchSaga() {
  const { activeChatSide }: ChatsState = yield select(state => state.chats);
  if (activeChatSide === ChatSideType.ON_HOLD)
    yield fork(startFetchChatsOnHold);
  if (activeChatSide === ChatSideType.REQUEST)
    yield fork(startFetchChatsRequestSaga);
  if (activeChatSide === ChatSideType.BOT) yield fork(startFetchChatsBotSaga);
}

function* fetchChatsSearchAdvancedSaga() {
  const { contactName = '' } = yield select(activeSideFiltersSelector);
  yield put(fetchSearchChats(contactName));
}

function* increaseOffsetChatsOnHoldSaga() {
  const { totalFilteredChatsOnHold, tray }: ChatsState['chatsOnHold'] =
    yield select((state: RootState) => state.chats.chatsOnHold);
  const qtyChatsOnHold = tray?.filter(({ show = false }) => show)?.length;
  if (totalFilteredChatsOnHold > qtyChatsOnHold)
    yield fork(startFetchChatsOnHold);
}

function* increaseOffsetChatsRequestSaga() {
  const { totalFilteredChatRequests, tray }: ChatsState['chatsRequest'] =
    yield select((state: RootState) => state.chats.chatsRequest);
  const qtyChatsRequest = tray?.filter(({ show = false }) => show)?.length;
  if (totalFilteredChatRequests > qtyChatsRequest)
    yield fork(startFetchChatsRequestSaga);
}

function* increaseOffsetChatsBotSaga() {
  const showBotsInbox: boolean = yield select(
    getConsolePreferenceShowBotsInbox
  );
  if (!showBotsInbox) return;

  const { totalFilteredChatBot, tray }: ChatsState['chatsBot'] = yield select(
    (state: RootState) => state.chats.chatsBot
  );
  const qtyChatsBot = tray?.filter(({ show = false }) => show)?.length;
  if (totalFilteredChatBot > qtyChatsBot) yield fork(startFetchChatsBotSaga);
}

export default function* chatsSaga() {
  yield takeLeading(increaseOffsetChatsOnHold, increaseOffsetChatsOnHoldSaga);
  yield takeLeading(increaseOffsetChatsRequest, increaseOffsetChatsRequestSaga);
  yield takeLeading(increaseOffsetChatsBot, increaseOffsetChatsBotSaga);

  yield takeLatest(fetchSearchChats, fetchChatsSearchSaga);
  yield takeLatest(fetchTransformTextAI, fetchTransformTextAISaga);

  yield takeLatest(fetchCRMbutton, fetchCRMbuttonSaga);
  yield takeLatest(fetchCRMinfo, fetchCRMinfoSaga);
  yield takeLatest(fetchNotificationTimeLimit, fetchNotificationTimeLimitSaga);

  yield throttle(1000, fetchChatTotals, fetchChatTotalsSaga);
  yield takeLeading(
    fetchChatSideTotals,
    // eslint-disable-next-line func-names
    function* (action: ReturnType<typeof fetchChatSideTotals>) {
      const filters = (yield select((state: RootState) =>
        chatsSideFiltersSelector(state)(action.payload)
      )) as ChatsFilters;
      yield call(fetchChatSideTotalsSaga, action.payload, filters);
    }
  );

  yield throttle(6000, fetchStartConsole, fetchStartConsoleSaga);

  yield debounce(
    300,
    [chatSearchActions.changeChatsFilters, chatSearchActions.cleanChatsFilters],
    fetchChatsSearchAdvancedSaga
  );
}
